import {
  eachDayOfInterval,
  endOfDay,
  format,
  getDay,
  parseISO,
  startOfDay,
} from 'date-fns';
import { DayOfWeek } from '../models/OperationHours.ts';
import { DOW_ZH } from './ParkingRateCardUtils.ts';

// ─── Constants ────────────────────────────────────────────────────────────────

export const DAYS = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];
export const DAY_SHORT = ['一', '二', '三', '四', '五', '六', '日'];
export const DOW_ORDER: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

// ─── Time / slot helpers ────────────────────────────────────────────────────────

export const fmt = (s: number): string =>
  `${String(Math.floor(s / 2)).padStart(2, '0')}:${s % 2 ? '30' : '00'}`;

/** 後端不接受 "24:00"，將其替換為 "23:59" */
export const fmtEnd = (s: number): string => {
  const t = fmt(s);
  return t === '24:00' ? '23:59' : t;
};

export const timeToSlot = (t: string): number => {
  if (t === '23:59' || t === '23:59:00' || t === '23:59:59') return 48; // 後端以 23:59 表示 24:00（全天末尾），對應 slot 48
  const [h, m] = t.split(':').map(Number);
  return h * 2 + (m >= 30 ? 1 : 0);
};

/**
 * "HH:mm" 起訖時間差幾分鐘；end < start 視為跨日（例如 22:00~08:00 = 600）。
 * endTime 是 "23:59" 時視為 24:00（全天末尾）而非字面上的 23 點 59 分，跟
 * timeToSlot 的慣例一致——不然 08:00~23:59 會少算 1 分鐘變成 959 而不是 960。
 */
export const crossDayDurationMinutes = (
  startTime: string,
  endTime: string,
): number => {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const startMinutes = sh * 60 + sm;
  const endMinutes = endTime === '23:59' ? 1440 : eh * 60 + em;
  const diff = endMinutes - startMinutes;
  return diff > 0 ? diff : diff + 1440;
};

// ─── Date helpers ───────────────────────────────────────────────────────────────

export const dateToStr = (d: Date): string => format(d, 'yyyy-MM-dd');

const ISO_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

/** 一天的開始（00:00:00），格式化為 effectivePeriod 用的 ISO 字串 */
export const toStartOfDayISO = (d: Date): string =>
  format(startOfDay(d), ISO_DATETIME_FORMAT);

/** 一天的結束（23:59:59），格式化為 effectivePeriod 用的 ISO 字串；接受 Date 或 "YYYY-MM-DD" 字串 */
export const toEndOfDayISO = (dateOrStr: Date | string): string => {
  const d = typeof dateOrStr === 'string' ? parseISO(dateOrStr) : dateOrStr;
  return format(endOfDay(d), ISO_DATETIME_FORMAT);
};

export function getDatesInRange(start: string, end: string): string[] {
  return eachDayOfInterval({ start: parseISO(start), end: parseISO(end) }).map(
    (d) => format(d, 'yyyy-MM-dd'),
  );
}

export const dateToLabel = (s: string): string => {
  const [, m, d] = s.split('-');
  const dow = getDay(parseISO(s)); // 0=Sun … 6=Sat
  return `${parseInt(m)}/${parseInt(d)}\n週${DOW_ZH[dow]}`;
};

/**
 * 給定 effectivePeriod 的起訖日期字串（YYYY-MM-DD），
 * 計算落在此區間內的 DOW 索引集合（Mon=0 … Sun=6，依序排列）。
 * 若區間 ≥ 7 天則回傳完整的 [0,1,2,3,4,5,6]。
 */
export function computeDowSubset(startStr: string, endStr: string): number[] {
  const dates = getDatesInRange(startStr, endStr);
  if (dates.length >= 7) return [0, 1, 2, 3, 4, 5, 6];
  const dowSet = new Set<number>();
  dates.forEach((s) => {
    const jsDay = getDay(parseISO(s)); // 0=Sun … 6=Sat
    dowSet.add(jsDay === 0 ? 6 : jsDay - 1); // Mon=0 … Sun=6
  });
  return [0, 1, 2, 3, 4, 5, 6].filter((i) => dowSet.has(i));
}
